import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { handleApiError, handleApiSuccess } from "./apiErrorHandler";
import { InputOtp } from "primereact/inputotp";
import axiosInstance from "../services/axiosInstance";
/**
 * OperatorAuthDialog
 *
 * Props:
 *  - visible (bool)
 *  - onSuccess(operatorPayload)
 *  - onCancel()
 *  - orgSettings (object)  // from backend or localStorage
 *
 * operatorPayload = {
 *   operator_id,
 *   operator_name,
 *   verification_method
 * }
 */
const OperatorAuthDialog = ({
  visible,
  onSuccess,
  onCancel,
  orgSettings = {},
}) => {

  const pinRef = useRef(null);
  const qrRef = useRef(null);

  const [pin, setPin] = useState("");
  const [qr, setQr] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(null); // 'PIN' | 'QR'

  const operatorCfg =
    orgSettings?.operator_authentication || { enabled: false };

  const allowPIN =
    operatorCfg?.methods?.includes("PIN") ?? true;
  const allowQR =
    operatorCfg?.methods?.includes("QR") ?? false;

  // Autofocus logic
  useEffect(() => {
    if (!visible) return;

    if (allowPIN && !allowQR) setMode("PIN");
    else if (!allowPIN && allowQR) setMode("QR");
    else if (allowPIN && allowQR) setMode("PIN");

  }, [visible, allowPIN, allowQR]);

  // Reset on open
  useEffect(() => {
    if (visible) {
      setPin("");
      setQr("");
      setLoading(false);
    }
  }, [visible]);

  useEffect(() => {
    if (mode === "PIN" && pin.length === 4) {
      verifyOperator("PIN", pin);
    }
  }, [pin, mode]);

  const verifyOperator = async (method, value) => {
    if (!value) {
      handleApiError({Error: "Please enter PIN or scan QR"});
      return;
    }

    try {
      setLoading(true);

      const res = await axiosInstance.post(
        "/api/operator/verify/",
        {
          method,
          value,
        }
      );    
      onSuccess({
        operator_id: res.data.operator_id,
        operator_name: res.data.operator_name,
        verification_method: method,
      });
    } catch (err) {
      console.log("now here2", err);
      handleApiError(err);
      setLoading(false);
      setPin("");
      setQr("");
      setTimeout(() => {
        if (allowPIN) pinRef.current?.focus();
        else if (allowQR) qrRef.current?.focus();
      }, 200);
    }
  };

  const handleQrKeyDown = (e) => {
    // Barcode scanners usually end with Enter
    if (e.key === "Enter") {
      e.preventDefault();
      verifyOperator("QR", qr);
    }
  };

  return (
    <>

      <Dialog
        header="Confirm Operator"
        visible={visible}
        modal
        closable={false}
        draggable={false}
        style={{ width: "420px" }}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={onCancel}
              disabled={loading}
            />
          </div>
        }
      >
        <div className="p-fluid">

        {/* MODE TOGGLE (only if both enabled) */}
        {allowPIN && allowQR && (
          <div className="flex justify-content-center gap-3 mb-3">
            <Button
              label="PIN"
              className={mode === "PIN" ? "p-button-primary" : "p-button-outlined"}
              onClick={() => {
                setMode("PIN");
                setPin("");
              }}
              disabled={loading}
            />
            <Button
              label="QR"
              className={mode === "QR" ? "p-button-primary" : "p-button-outlined"}
              onClick={() => {
                setMode("QR");
                setQr("");
              }}
              disabled={loading}
            />
          </div>
        )}

        {/* PIN MODE */}
        {mode === "PIN" && allowPIN && (
          <div className="field text-center">
            <label className="font-bold mb-2 block">
              Enter Operator PIN
            </label>

            <InputOtp
              ref={pinRef}
              value={pin}
              onChange={(e) => setPin(e.value)}
              length={4}
              autoFocus
              disabled={loading}
            />

            <small className="text-gray-500 block mt-2">
              Enter 4-character PIN
            </small>
          </div>
        )}

        {/* QR MODE */}
        {mode === "QR" && allowQR && (
          <div className="field">
            <label className="font-bold">
              Scan Operator QR
            </label>
            <InputText
              ref={qrRef}
              value={qr}
              onChange={(e) => setQr(e.target.value)}
              onKeyDown={handleQrKeyDown}
              placeholder="Scan QR code"
              disabled={loading}
              style={{ fontSize: "16px" }}
            />
            <small className="text-gray-500">
              Use barcode scanner or camera
            </small>
          </div>
        )}

        {!allowPIN && !allowQR && (
          <div className="text-center text-red-500">
            Operator authentication not configured
          </div>
        )}

      </div>

      </Dialog>
    </>
  );
};

export default OperatorAuthDialog;
